// Webrtc plugin.
import { sessionActions } from '../interface/actions'
import * as actionTypes from '../interface/actionTypes'
import watchSessionEvents from './session'

// Libraries.
import { fork, put, take } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'

/**
 * Sets up event listeners for Session Manager Events.
 * The events are turned into actions and dispatched
 *    to be handled by redux elsewhere.
 * @method watchSessionManagerEvents
 * @param  {Object} manager The webRTC Session Manager.
 * @param  {Object} webRTC The webRTC stack.
 */
export default function * watchSessionManagerEvents (manager, webRTC) {
  /**
   * Create an event channel between the Session Manager and redux-saga.
   * This pushes its events into the redux-saga context.
   */
  const managerChannel = eventChannel(emit => {
    return setListeners(manager, emit)
  })

  while (true) {
    const { session, action } = yield take(managerChannel)

    // If it is a new Session, watch for its events.
    if (action.type === actionTypes.SESSION_ADDED) {
      yield fork(watchSessionEvents, session, webRTC)
    }

    // Dispatch the action directly.
    yield put(action)
  }
}

/**
 * Sets up listeners for the Session Manager events.
 * Contains logic for how specific webRTC events should be
 *    parsed before being sent elsewhere. Split into a
 *    stand-alone function in order to be re-usable.
 * @method setListeners
 * @param  {Object}   manager The Session Manager.
 * @param  {Function} emit    Function that receives the parsed events.
 * @param  {Any}      [END='END'] The "end of lifetime" symbol.
 * @return {Function} The unsubscribe function.
 */
export function setListeners (manager, emit, END = 'END') {
  // Handle new Session.
  const sessionAdded = id => {
    const session = manager.get(id)
    const state = session.getState()
    delete state.id

    emit({
      session: session,
      action: sessionActions.sessionAdded(id, state)
    })
  }

  // Handle Sessions ending.
  const sessionRemoved = id => {
    emit({
      action: sessionActions.sessionRemoved(id)
    })
  }

  manager.on('session:new', sessionAdded)
  manager.on('session:removed', sessionRemoved)
  const unsubscribe = () => {
    manager.off('session:new', sessionAdded)
    manager.off('session:removed', sessionRemoved)
  }
  return unsubscribe
}
